// Was ein Unternehmen im Risiken-Reiter über sich ausweist — an EINER Stelle
// abgeleitet, weil es an ZWEI Stellen erscheint: als zentrierter Block, wenn
// nur dieses eine Unternehmen betrachtet wird, und als Kästchen im Organigramm,
// sobald der Unternehmensverbund gezeigt wird. Beide müssen dasselbe sagen —
// sonst hätte dieselbe Firma je nach Ebene eine andere Branche.

import { findLeaf, formatBranchePath, type BrancheLeaf } from "../../data/brancheClassification";

/** Trennzeichen der Mehrfachauswahl in `Contact.unternehmensfunktion`. */
const FUNKTION_TRENNER = /[;,]/;

/**
 * Die gewählten Unternehmensfunktionen als Liste.
 *
 * Komma wird mitgelesen, obwohl gespeichert wird mit „; ": ältere Einträge und
 * die Anlage-Vorbelegung trennen mit Komma, und ein so erfasster Wert darf im
 * Organigramm nicht als ein einziger langer Chip stehen.
 */
export function funktionenAus(roh: string | null | undefined): string[] {
  return (roh ?? "")
    .split(FUNKTION_TRENNER)
    .map((t) => t.trim())
    .filter(Boolean);
}

/** Eine Branche der Firma, aufgelöst gegen den Katalog. */
export type BrancheAnzeige = {
  id: number;
  /** Ebene 1 — bestimmt das Bildzeichen. */
  ebene1: string;
  /** Vollständiger Pfad „Ebene 1 / Gruppe / Spezialisierung". */
  pfad: string;
};

/**
 * Alle Branchen einer Firma in EINER Liste: die Hauptbranche
 * (`Contact.brancheClassId`) und die weiteren Zuordnungen
 * (`ContactBrancheAssignment`).
 *
 * Bewusst gleichrangig: eine Firma, die Handel UND Werkstatt führt, trägt die
 * Risiken beider — welche zuerst erfasst wurde, sagt darüber nichts. Doppelte
 * IDs fallen weg, unauflösbare (gelöschter Katalogeintrag) ebenfalls, sonst
 * stünde dort eine leere Zeile.
 */
export function branchenAus(
  rows: BrancheLeaf[],
  brancheClassId: number | null | undefined,
  weitere: Array<{ brancheClassId: number }> | number[] | null | undefined,
): BrancheAnzeige[] {
  const weitereIds = (weitere ?? []).map((w) =>
    typeof w === "number" ? w : w.brancheClassId,
  );
  const ids = Array.from(
    new Set([brancheClassId, ...weitereIds].filter((id): id is number => id != null)),
  );
  return ids
    .map((id) => {
      const leaf = findLeaf(rows, id);
      const pfad = formatBranchePath(leaf);
      return leaf && pfad ? { id, ebene1: leaf.ebene1, pfad } : null;
    })
    .filter((b): b is BrancheAnzeige => b != null);
}

/** Erste Zeile eines Kästchens: „Name — Straße, PLZ Ort". */
export function nameMitAnschrift(
  name: string | null | undefined,
  anschrift: string | null | undefined,
): string {
  const n = (name ?? "").trim();
  const a = (anschrift ?? "").trim();
  return a ? `${n} — ${a}` : n;
}
